/**
 * Shared media URL utilities for worksheet question image resolution.
 * Both AiAnswerSheetUpload and WorkSheetTake previously duplicated these.
 */
import api, { getProxyBaseUrl } from "../api";

/**
 * Resolve a raw URL (possibly an R2 CDN URL) to an API proxy path.
 * Converts pub-*.r2.dev URLs to /proxy/visual/{key} to avoid CORS issues.
 */
export function makeAbsoluteUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/) ||
    trimmed.match(/^https?:\/\/[^/]+\.r2\.cloudflarestorage\.com\/(.+)$/);
  if (r2Match) {
    const baseURL = (getProxyBaseUrl ? getProxyBaseUrl() : null) || api.defaults?.baseURL || "";
    return `${baseURL.replace(/\/$/, "")}/proxy/visual/${r2Match[1]}`;
  }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  const baseURL = (getProxyBaseUrl ? getProxyBaseUrl() : null) || api.defaults?.baseURL || "";
  return baseURL ? `${baseURL.replace(/\/$/, "")}/${trimmed.replace(/^\//, "")}` : trimmed;
}

/**
 * Extract the best image URL from a question object, checking all known field locations.
 */
export function resolveQuestionImageUrl(question) {
  if (!question) return null;
  const raw =
    question.visual_image_url ||
    question.image_url ||
    question.visual_url ||
    (typeof question.visual === "string" ? question.visual : null) ||
    question.visual?.url ||
    question.visual?.r2_url ||
    question.visual?.public_url ||
    question.visual?.image_url ||
    null;
  const resolved = makeAbsoluteUrl(raw);
  if (resolved) return resolved;
  // Inline SVG fallback (visual type svg-inline, stored when R2 upload is unavailable)
  const svg = question.visual?.svg;
  if (typeof svg === "string" && svg.trim().startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
  return null;
}
